import { Trash2, Upload } from "lucide-react";
import { useRef } from "react";
import { toast } from "sonner";
import {
  useRemoveAvatar,
  useSetAvatar,
} from "@/features/members/hooks/useProfileMutations";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";

/** Profile photo control. The source app's canvas cropper is still a known
 * gap (docs/port-plan.md) — this uploads the file as picked.
 *
 * Two photos can exist: the one Clerk copied from Google (or another
 * provider) at sign-up, and an AFELL upload made here — the only photo
 * control on the site. The upload wins while it exists; removing it falls
 * back to the account photo, which is why Remove appears only for an upload:
 * there is nothing of ours to remove from the other. */
export function AvatarUpload({
  currentUrl,
  name,
  source = null,
  accountUrl = null,
}: {
  currentUrl: string | null;
  name: string;
  source?: "upload" | "account" | null;
  accountUrl?: string | null;
}) {
  const setAvatar = useSetAvatar();
  const removeAvatar = useRemoveAvatar();
  const fileInput = useRef<HTMLInputElement>(null);
  const caption =
    source === "account"
      ? "The photo from the account you signed up with. Upload a different one to use on AFELL."
      : source === "upload" && accountUrl
        ? "Your AFELL photo. Remove it to go back to the photo from your account."
        : null;

  return (
    <div className="flex items-center gap-4">
      <Avatar className="size-16">
        <AvatarImage src={currentUrl ?? undefined} alt={name} />
        <AvatarFallback className="text-xl">{name.charAt(0)}</AvatarFallback>
      </Avatar>
      <div className="space-y-1.5">
      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => fileInput.current?.click()}
          disabled={setAvatar.isPending}
        >
          {setAvatar.isPending ? (
            <Spinner data-slot="icon" />
          ) : (
            <Upload data-slot="icon" />
          )}
          {setAvatar.isPending ? "Uploading…" : "Upload photo"}
        </Button>
        {source === "upload" && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() =>
              removeAvatar.mutate(undefined, {
                onError: (err) => toast.error(err.message),
              })
            }
            disabled={removeAvatar.isPending}
          >
            <Trash2 data-slot="icon" /> Remove
          </Button>
        )}
      </div>
      {caption && <p className="text-muted-foreground text-xs">{caption}</p>}
      </div>
      <input
        ref={fileInput}
        type="file"
        accept="image/*"
        hidden
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) {
            setAvatar.mutate(file, {
              onSuccess: () => toast.success("Photo updated"),
              onError: (err) => toast.error(err.message),
            });
          }
          e.target.value = "";
        }}
      />
    </div>
  );
}

export default AvatarUpload;
